import { Address } from '../types/address';
import { AddressWithComponents } from '../types/smarty-streets';
import { InputWithLabel } from '@/shadcn-ui/input-with-label';
import { Label } from '@/shadcn-ui/label';
import { useState } from 'react';
import SmartyStreetsInput from './smarty-streets-input';
import Spacer from '@/common/components/spacer';
import USTerritorySelect from './us-territory-select';

export interface AddressErrors {
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
}

export interface AddressFormFragmentProps {
  smartyStreetsEmbeddedKey: string;
  addressErrors?: AddressErrors;
  onAddressChange?: (addressData: Partial<Address>) => void;
}

export default function AddressFormFragment({
  smartyStreetsEmbeddedKey,
  addressErrors,
  onAddressChange,
}: AddressFormFragmentProps) {
  const [address2, setAddress2] = useState<string | undefined>();
  const [city, setCity] = useState<string | undefined>();
  const [state, setState] = useState<string | undefined>();
  const [zipcode, setZipcode] = useState<string | undefined>();

  function handleSuggestionSelect(suggestions: AddressWithComponents) {
    const newAddress1 = suggestions.components.streetLine;
    const newAddress2 = suggestions.components.secondary;
    const newCity = suggestions.components.city;
    const newState = suggestions.components.state;
    const newZipcode = suggestions.components.zipcode;

    setAddress2(newAddress2);
    setCity(newCity);
    setState(newState);
    setZipcode(newZipcode);

    // Notify parent of changes
    if (onAddressChange) {
      onAddressChange({
        address1: newAddress1,
        address2: newAddress2,
        city: newCity,
        state: newState,
        zipcode: newZipcode,
      });
    }
  }

  // Notify parent of changes to each individual field
  const handleAddress2Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAddress2(value);
    if (onAddressChange) onAddressChange({ address2: value });
  };

  const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCity(value);
    if (onAddressChange) onAddressChange({ city: value });
  };

  const handleStateChange = (newState?: string) => {
    setState(newState);
    if (onAddressChange) onAddressChange({ state: newState });
  };

  const handleZipcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setZipcode(value);
    if (onAddressChange) onAddressChange({ zipcode: value });
  };

  return (
    <div className="flex flex-col gap-4">
      {/* address1 */}
      <div>
        <Label id="address1-label" variant="light">
          Street address
        </Label>
        <Spacer variant="height4" />
        <SmartyStreetsInput
          name="address1"
          limitToStreetLine={true}
          errorText={addressErrors?.address1}
          smartyStreetsEmbeddedKey={smartyStreetsEmbeddedKey}
          handleSuggestionSelectDelegate={handleSuggestionSelect}
          onChange={(e) => {
            if (onAddressChange) onAddressChange({ address1: e.target.value });
          }}
        />
      </div>

      {/* address2 */}
      <div>
        <InputWithLabel
          name="address2"
          label="Apt, suite, bldg. (Optional)"
          type="text"
          value={address2}
          errorText={addressErrors?.address2}
          onChange={handleAddress2Change}
          className="w-full"
        />
      </div>

      {/* city */}
      <div>
        <InputWithLabel
          name="city"
          label="City"
          type="text"
          value={city}
          errorText={addressErrors?.city}
          onChange={handleCityChange}
          className="w-full"
        />
      </div>

      {/* state */}
      <div>
        <USTerritorySelect
          selectedState={state}
          setSelectedState={handleStateChange}
          errorText={addressErrors?.state}
        />
      </div>

      {/* zipcode */}
      <div>
        <InputWithLabel
          name="zipcode"
          label="Zip code"
          type="text"
          value={zipcode}
          errorText={addressErrors?.zipcode}
          onChange={handleZipcodeChange}
          className="w-full"
        />
      </div>
    </div>
  );
}
